/**
 * Reactive state of the application store (spec §4) with the persisted settings of §5.
 * Every field name and default mirrors the legacy store so the components port 1:1, and every
 * localStorage key/value is byte-identical so existing users keep their settings.
 */
import { SvelteSet } from 'svelte/reactivity';
import { isSortMode } from '$lib/mail/sort';
import type { ConnectionState } from '$lib/realtime/sse';
import type {
  AutoAdvance,
  ContactRec,
  Density,
  FolderRec,
  MessageRow,
  MutedThread,
  PendingSend,
  SavedSearch,
  Screen,
  SearchChip,
  SortMode,
  TemplateRec,
  TrustedSender,
} from '$lib/types';

export const LS = Object.freeze({
  notify: 'tik.notify',
  notifyMark: 'tik.notifyMark',
  sort: 'tik.sort',
  threads: 'tik.threads',
  density: 'tik.density',
  advance: 'tik.advance',
  undoSec: 'tik.undoSec',
  recent: 'tik.recent',
  theme: 'tik.theme',
  rail: 'tik.rail',
});

function ls(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

export function lsDel(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export interface UndoSlot {
  opId: number;
  op: string;
  folder: string;
  dest?: string;
  rows: MessageRow[];
  delta: { wasUnseen: number; wasSeen: number; count: number };
  uidMap?: [number, number][] | null;
}

export type NotifyPerm = NotificationPermission | 'unsupported';

/** Reactive fields only; behaviour lives in the modules and in `MailStore`. */
export class MailState {
  user = $state<string | null>(null);
  screen = $state<Screen>({ name: 'login' });
  folders = $state<FolderRec[]>([]);
  msgs = $state<MessageRow[]>([]);
  currentFolder = $state('INBOX');
  syncing = $state(false);
  offline = $state(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  liveDown = $state(false);
  booted = $state(false);
  /** SSE connection state mirrored from `LiveClient`. */
  connection = $state<ConnectionState>('reconnecting');
  watcherState = $state<'idle' | 'reconnecting' | 'auth_error' | 'down'>('idle');

  notifyWanted = $state(ls(LS.notify) === '1');
  notifyPerm = $state<NotifyPerm>(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  pushOn = $state(false);
  pushDevices = $state(0);
  pushWatching = $state(false);
  pushBusy = $state(false);
  canPush = $state(false);
  needsInstall = $state(false);
  /** Highest INBOX uid already considered for a page notification. */
  notifyMark = Number(ls(LS.notifyMark) || 0);

  toast = $state<string | null>(null);
  toastLeft = 0;
  toastAt = 0;
  undoToken = $state<string | null>(null);
  undoSlot: UndoSlot | null = null;

  searchQ = $state('');
  searchResults = $state<MessageRow[] | null>(null);
  searching = $state(false);
  searchMatched = $state(0);
  searchError = $state<string | null>(null);
  searchRanked = $state('date');
  searchChips = $state<SearchChip[]>([]);
  searchScanned = $state<number | null>(null);
  searchTruncated = $state(false);

  contacts = $state<ContactRec[]>([]);
  selection = new SvelteSet<number>();
  selectionFolder = $state<string | null>(null);
  lastAnchorKey: string | null = null;

  moveSheet = $state(false);
  viewerOpen = $state(false);
  viewSheet = $state(false);
  foldersSheet = $state(false);

  folderTotal = $state(0);
  folderLoaded = $state(0);
  lastSyncError = $state<{ folder: string; at: number } | null>(null);
  cursorKey = $state<string | null>(null);
  lastCursorIndex = 0;
  listScrollTop = 0;

  sortMode = $state<SortMode>(isSortMode(ls(LS.sort)) ? (ls(LS.sort) as SortMode) : 'date');
  threadView = $state(ls(LS.threads) !== '0');
  density = $state<Density>(ls(LS.density) === 'compact' ? 'compact' : 'cozy');
  autoAdvance = $state<AutoAdvance>(['next', 'prev'].includes(ls(LS.advance) || '') ? (ls(LS.advance) as AutoAdvance) : 'list');
  undoSec = $state(Math.min(20, Math.max(0, Number(ls(LS.undoSec) ?? 10))));

  splitUnread = $state<{ personal: number; list: number } | null>(null);
  mutedCount = $state(0);
  mutedThreads = $state<MutedThread[]>([]);
  knownLabels = $state<string[]>([]);
  labelCounts = $state<Record<string, number>>({});
  followupCount = $state(0);
  followupOn = $state(false);

  dragUids = $state<number[] | null>(null);
  dragFrom = $state<string | null>(null);

  pendingSend = $state<{ id: string; left: number } | null>(null);
  pendingSends = $state<PendingSend[]>([]);
  snoozeTarget = $state<number[] | null>(null);
  trustedSenders = $state<TrustedSender[]>([]);
  savedSearches = $state<SavedSearch[]>([]);
  templates = $state<TemplateRec[]>([]);
  recentSearches = $state<string[]>(readRecent());
  lastBulk = $state<unknown>(null);
  composeUnder = $state<Screen | null>(null);
  quota = $state<{ supported: boolean; used: number; limit: number } | null>(null);

  // ---- non-reactive helpers (spec §4 "Non-reactive helpers")
  syncingHash = false;
  lastLocalAct = 0;
  static readonly DWELL_MS = 1200;

  /** `notifyWanted && permission granted`. */
  get notifyEnabled(): boolean {
    return this.notifyWanted && typeof Notification !== 'undefined' && Notification.permission === 'granted';
  }

  // ---- settings setters (persist the exact legacy keys/values)
  setSortMode(mode: SortMode): void {
    this.sortMode = mode;
    lsSet(LS.sort, mode);
  }
  setThreadView(on: boolean): void {
    this.threadView = on;
    lsSet(LS.threads, on ? '1' : '0');
  }
  setDensity(d: Density): void {
    this.density = d;
    lsSet(LS.density, d);
    this.listScrollTop = 0;
  }
  setAutoAdvance(a: AutoAdvance): void {
    this.autoAdvance = a;
    lsSet(LS.advance, a);
  }
  /** `≤0 → 0`, else `min(20, max(5, floor))`. */
  setUndoSec(n: number): void {
    this.undoSec = n <= 0 ? 0 : Math.min(20, Math.max(5, Math.floor(n)));
    lsSet(LS.undoSec, String(this.undoSec));
  }
  setNotifyMark(uid: number): void {
    this.notifyMark = uid;
    lsSet(LS.notifyMark, String(uid));
  }
  refreshNotifyPerm(): void {
    this.notifyPerm = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  }
}

function readRecent(): string[] {
  try {
    const v = JSON.parse(ls(LS.recent) || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 8) : [];
  } catch {
    return [];
  }
}

/** Theme helpers (spec §3): stored choice, else `prefers-color-scheme`. Written on every apply. */
export function readTheme(): 'dark' | 'light' {
  const v = ls(LS.theme);
  if (v === 'dark' || v === 'light') return v;
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: 'dark' | 'light'): void {
  if (typeof document !== 'undefined') document.documentElement.classList.toggle('tik-dark', theme === 'dark');
  lsSet(LS.theme, theme);
}

export function readRail(): 'mini' | 'full' {
  return ls(LS.rail) === 'mini' ? 'mini' : 'full';
}

export function writeRail(v: 'mini' | 'full'): void {
  lsSet(LS.rail, v);
}
